import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import { alpha, useTheme } from "@mui/material";
import { PlanItemObject } from "../utils/backend-utils";

interface PlanSidebarProps {
  plan: PlanItemObject[];
  currentStep?: number;
  executionStartedAt?: string;
}

function formatDuration(seconds: number | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

function diffSeconds(a?: string, b?: string): number | undefined {
  if (!a || !b) return undefined;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta) || Number.isNaN(tb)) return undefined;
  return Math.max(0, Math.round((tb - ta) / 1000));
}

export function PlanSidebar({ plan, currentStep, executionStartedAt }: PlanSidebarProps) {
  const theme = useTheme();

  const completedCount = plan.filter((s) => s.status === "COMPLETED" || s.completed_at).length;
  const total = plan.length;
  const pct = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  const totalDurationS = (() => {
    if (!executionStartedAt) return undefined;
    const latest = plan
      .map((s) => s.completed_at || s.started_at)
      .filter(Boolean)
      .sort()
      .pop();
    if (!latest) return undefined;
    return diffSeconds(executionStartedAt, latest);
  })();


  return (
    <Box
      sx={{
        width: { xs: 260, md: 320 },
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        borderRight: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
        bgcolor: alpha(theme.palette.background.default, 0.5),
        overflow: "hidden",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 0.75 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
            Plan progress
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: "0.72rem" }}
          >
            {completedCount} / {total}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 4,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.divider, 0.4),
            "& .MuiLinearProgress-bar": {
              bgcolor: pct === 100 ? theme.palette.success.main : theme.palette.primary.main,
              borderRadius: 2,
            },
          }}
        />
        {totalDurationS != null && (
          <Typography
            variant="caption"
            sx={{ display: "block", color: theme.palette.text.secondary, mt: 0.75, fontSize: "0.7rem" }}
          >
            {formatDuration(totalDurationS)} elapsed
          </Typography>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        {plan.map((step, i) => {
          const isDone = step.status === "COMPLETED" || Boolean(step.completed_at);
          const isCurrent = !isDone && currentStep === i + 1;
          const color =
            isDone ? theme.palette.success.main
            : isCurrent ? theme.palette.primary.main
            : theme.palette.text.disabled;
          const dur =
            step.duration_seconds ??
            diffSeconds(step.started_at, step.completed_at);

          const Icon =
            isDone ? CheckCircleIcon
            : isCurrent ? PlayArrowIcon
            : RadioButtonUncheckedIcon;


          return (
            <Box
              key={String(step.id ?? i)}
              sx={{
                display: "flex",
                gap: 1.25,
                alignItems: "flex-start",
                px: 2,
                py: 1,
                position: "relative",
                bgcolor: isCurrent ? alpha(theme.palette.primary.main, 0.06) : "transparent",
                borderLeft: isCurrent ? `3px solid ${theme.palette.primary.main}` : "3px solid transparent",
              }}
            >
              <Icon sx={{ fontSize: 18, color, mt: 0.25, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: isCurrent ? 600 : isDone ? 400 : 400,
                    color: isDone ? theme.palette.text.secondary : theme.palette.text.primary,
                    lineHeight: 1.35,
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  <Box component="span" sx={{ color, fontWeight: 700, mr: 0.5 }}>{i + 1}.</Box>
                  {step.task}
                </Typography>
                {(isDone || isCurrent) && (
                  <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    {dur != null && (
                      <Chip
                        size="small"
                        label={formatDuration(dur) || "—"}
                        sx={{ borderRadius: "4px", height: 18, fontSize: "0.65rem", bgcolor: "transparent", border: `1px solid ${alpha(color, 0.4)}`, color: theme.palette.text.secondary }}
                      />
                    )}
                    {String(step.confidence || "HIGH").toUpperCase() === "LOW" && (
                      <Chip
                        size="small"
                        label="LOW confidence"
                        sx={{ borderRadius: "4px", height: 18, fontSize: "0.6rem", bgcolor: alpha(theme.palette.warning.main, 0.15), color: theme.palette.warning.dark }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
